import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Select, Spin, notification } from 'antd';
import usePageTitle from '../../../core/hooks/UsePageTitle';
import useMaterias from '../../../core/hooks/UseMaterias';
import DefaultLayout from '../../layouts/default/Default.layout';
import OcclusionEditor from '../../components/OcclusionEditor/OcclusionEditor';
import CompositorPreview from '../../components/Revisao/CompositorPreview';
import type { FacePrevia } from '../../components/Revisao/cartaoDaPrevia';
import FormatToolbar from '../../components/Revisao/FormatToolbar';
import AlvoDeImagemDaFace, {
  type AlvoDeImagemDaFaceHandle,
} from '../../components/Revisao/imagem/AlvoDeImagemDaFace';
import MetadadosBar from '../../components/Revisao/MetadadosBar';
import CriadosAgora from '../../components/Revisao/CriadosAgora';
import {
  deveCriarReverso,
  facesDoEnvio,
  listarCriadosAgora,
  metadadosDasTags,
  montarCartaoCriado,
  registrarCriadosAgora,
  tagsParaGravar,
  type CartaoCriadoAgora,
} from '../../components/Revisao/metadadosCartao';
import {
  TELHAS_AUTORIA,
  formaInicial,
  layoutDaForma,
  rotulosDaForma,
  type FormaAutoria,
} from '../../components/Revisao/formaAutoria';
import { inserirFormato, type FormatoInserivel } from '../../components/Revisao/formatacaoCartao';
import { MASK_REQUIRED_MESSAGE } from '../../components/OcclusionEditor/occlusionStudy';
import { modoDaLeitura, occlusionModeNoFio } from '../../components/Revisao/editorNaRevisaoContrato';
import ReviewService from '../../../sdk/services/ReviewService';
import QuestaoService from '../../../sdk/services/QuestaoService';
import { OcclusionStudyMode, ReviewCard, ReviewMask } from '../../../sdk/@types/Revisao';

type Tipo = 'flashcard' | 'question';

interface QuestionOption {
  value: string;
  label: string;
  subjectId: string;
  keyword: string;
}

const tipoOptions: { label: string; value: Tipo }[] = [
  { label: 'Flashcard', value: 'flashcard' },
  { label: 'Questão do banco', value: 'question' },
];

const campoClass =
  'w-full rounded-input border border-edge bg-surface px-3 py-2 text-sm text-ink';

function lerSelecao(value: string, el: HTMLTextAreaElement | null): [number, number] {
  return [el?.selectionStart ?? value.length, el?.selectionEnd ?? value.length];
}

export default function NovoCartaoView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  usePageTitle(isEdit ? 'Editar cartão' : 'Novo cartão');

  const { materias, fetchMaterias } = useMaterias();

  const [tipo, setTipo] = useState<Tipo>('flashcard');
  const [forma, setForma] = useState<FormaAutoria>('texto');
  const layout = layoutDaForma(forma);
  const rotulos = rotulosDaForma(forma);
  const [subjectId, setSubjectId] = useState<string | undefined>();
  const [tags, setTags] = useState<string[]>([]);
  const [baralho, setBaralho] = useState('');
  const [criarReverso, setCriarReverso] = useState(false);
  const [criados, setCriados] = useState<CartaoCriadoAgora[]>(() => listarCriadosAgora());
  const [tagOptions, setTagOptions] = useState<{ value: string; label: string }[]>([]);
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [frontImageKey, setFrontImageKey] = useState<string | undefined>();
  const [masks, setMasks] = useState<ReviewMask[]>([]);
  const [studyMode, setStudyMode] = useState<OcclusionStudyMode>('ocultar-todas');
  const [facePrevia, setFacePrevia] = useState<FacePrevia>('frente');
  const [saving, setSaving] = useState(false);

  const frontRef = useRef<HTMLTextAreaElement>(null);
  const backRef = useRef<HTMLTextAreaElement>(null);
  const frontAlvoRef = useRef<AlvoDeImagemDaFaceHandle>(null);
  const backAlvoRef = useRef<AlvoDeImagemDaFaceHandle>(null);
  const formaButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selecaoPendente = useRef<{ campo: 'front' | 'back'; start: number; end: number } | null>(
    null,
  );

  const inserirNoCampo = (campo: 'front' | 'back', formato: FormatoInserivel) => {
    const el = campo === 'front' ? frontRef.current : backRef.current;
    const value = campo === 'front' ? front : back;
    const [start, end] = lerSelecao(value, el);
    const next = inserirFormato(value, start, end, formato);
    selecaoPendente.current = { campo, start: next.selectionStart, end: next.selectionEnd };
    if (campo === 'front') setFront(next.value);
    else setBack(next.value);
  };

  /** Chamada quando `AlvoDeImagemDaFace` termina de enviar a imagem daquela
   *  face — nunca antes do envio concluir (o envio em si vive inteiramente
   *  dentro do componente; esta função só sabe montar o token e gravá-lo no
   *  campo certo). */
  const inserirImagemNoCampo = (campo: 'front' | 'back', fileKey: string) => {
    const el = campo === 'front' ? frontRef.current : backRef.current;
    // O envio é assíncrono. `front`/`back` fechados no render do clique
    // ficam velhos se o autor digitou enquanto o arquivo subia. O textarea
    // controlado já tem o valor commitado — é ele que recebe o token.
    const value = el?.value ?? (campo === 'front' ? front : back);
    const [start, end] = lerSelecao(value, el);
    const token = `![imagem](/files/${fileKey})`;
    const next = value.slice(0, start) + token + value.slice(end);
    const selectionStart = start + token.length;
    // Mesmo `selecaoPendente` da barra: o useLayoutEffect devolve o cursor
    // depois que o React reescreve o valor e joga a seleção para o fim.
    selecaoPendente.current = { campo, start: selectionStart, end: selectionStart };
    if (campo === 'front') setFront(next);
    else setBack(next);
  };

  // question mode
  const [questionId, setQuestionId] = useState<string | undefined>();
  const [questionOptions, setQuestionOptions] = useState<QuestionOption[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // O React reescreve o valor do textarea no commit e joga o cursor para o fim.
  // A seleção volta aqui, depois desse commit, para o trecho que a barra inseriu.
  useLayoutEffect(() => {
    const pendente = selecaoPendente.current;
    if (!pendente) return;
    selecaoPendente.current = null;
    const el = pendente.campo === 'front' ? frontRef.current : backRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(pendente.start, pendente.end);
  });

  useEffect(() => {
    fetchMaterias();
  }, [fetchMaterias]);

  // edit mode: prefill from the card passed via navigation state, or fetch it
  useEffect(() => {
    if (!editId) return;
    const apply = (card: ReviewCard) => {
      setTipo('flashcard');
      setForma(formaInicial(card.layout));
      setSubjectId(card.subjectId);
      const meta = metadadosDasTags(card.tags);
      setBaralho(meta.baralho);
      setTags(meta.tags);
      setCriarReverso(false);
      setFront(card.front ?? '');
      setBack(card.back ?? '');
      setFrontImageKey(card.frontImageKey);
      setMasks(card.masks ?? []);
      setStudyMode(modoDaLeitura(card));
    };
    const fromState = (location.state as { card?: ReviewCard } | null)?.card;
    if (fromState && fromState.id === editId) {
      apply(fromState);
      return;
    }
    ReviewService.listTemplates()
      .then((page) => {
        const found = page.items.find((c) => c.id === editId);
        if (found) apply(found);
        else notification.error({ message: 'Cartão não encontrado.' });
      })
      .catch(() => notification.error({ message: 'Erro ao carregar o cartão.' }));
  }, [editId, location.state]);

  // Tag suggestions follow the selected matéria — once a subject is chosen we
  // only offer that subject's keywords; with none selected we fall back to all.
  useEffect(() => {
    const req = subjectId
      ? QuestaoService.getTagsByContext({ subjectIds: [subjectId] })
      : QuestaoService.buscarTagsDasPalavrasChaves();
    req
      .then((res) =>
        setTagOptions(
          (res ?? [])
            .map((t) => String(t.description ?? '').trim())
            .filter(Boolean)
            .map((d) => ({ value: d, label: d }))
        )
      )
      .catch(() => setTagOptions([]));
  }, [subjectId]);

  const handleQuestionSearch = useCallback((value: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!value.trim()) {
      setQuestionOptions([]);
      return;
    }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const questions = await QuestaoService.getQuestoesPorFiltro({ statement: value });
        setQuestionOptions(
          (questions ?? []).slice(0, 30).map((q: any) => ({
            value: String(q.id),
            label: `[${q.testingAgency?.name ?? ''}] ${(q.statement ?? '').slice(0, 80)}`,
            subjectId: q.subject?.id ? String(q.subject.id) : '',
            keyword: q.keyword ?? '',
          }))
        );
      } catch {
        setQuestionOptions([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  }, []);

  /** Picking a question pre-fills matéria/tags from the question itself. */
  const handleQuestionSelect = (value: string) => {
    setQuestionId(value);
    const picked = questionOptions.find((o) => o.value === value);
    if (!picked) return;
    if (picked.subjectId) setSubjectId(picked.subjectId);
    setTags(
      picked.keyword
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    );
  };

  const moverForma = (event: KeyboardEvent<HTMLDivElement>) => {
    const atual = TELHAS_AUTORIA.findIndex((telha) => telha.id === forma);
    if (atual < 0) return;

    let proximo: number;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      proximo = (atual + 1) % TELHAS_AUTORIA.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      proximo = (atual - 1 + TELHAS_AUTORIA.length) % TELHAS_AUTORIA.length;
    } else {
      return;
    }

    event.preventDefault();
    setForma(TELHAS_AUTORIA[proximo].id);
    formaButtonRefs.current[proximo]?.focus();
  };

  const validationError = (): string | null => {
    if (tipo === 'question') {
      if (!questionId) return 'Busque e selecione uma questão.';
      return null;
    }
    if (!subjectId) return 'Selecione a matéria.';
    if (layout === 'occlusion') {
      if (!frontImageKey) return 'Envie a imagem do cartão de oclusão.';
      if (masks.length === 0) return MASK_REQUIRED_MESSAGE;
      if (masks.some((m) => !m.label.trim()))
        return 'Toda marcação precisa de uma resposta escondida.';
      return null;
    }
    if (!front.trim()) return 'Preencha a frente do cartão.';
    if (!back.trim()) return 'Preencha o verso do cartão.';
    return null;
  };

  const handleSave = async () => {
    const error = validationError();
    if (error) {
      notification.warning({ message: error });
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await ReviewService.updateCard(editId!, {
          layout,
          subjectId: subjectId!,
          tags: tagsParaGravar(baralho, tags),
          front: layout === 'occlusion' ? undefined : front.trim(),
          back: back.trim() || undefined,
          frontImageKey: layout === 'occlusion' ? frontImageKey : undefined,
          masks: layout === 'occlusion' ? masks : undefined,
          occlusionMode: layout === 'occlusion' ? occlusionModeNoFio(studyMode) : undefined,
        });
        notification.success({ message: 'Cartão atualizado' });
        navigate('/admin/cartoes');
        return;
      }

      if (tipo === 'question') {
        try {
          await ReviewService.createCard({
            source: 'question',
            questionId: questionId!,
            subjectId: subjectId ?? '',
            tags,
          });
        } catch (err: any) {
          if (err?.response?.status === 409) {
            notification.info({ message: 'Essa questão já está na sua revisão.' });
            return;
          }
          throw err;
        }
        notification.success({
          message: 'Questão adicionada à revisão',
          description: 'Ela entra no seu próprio baralho — questões não viram template.',
        });
        navigate('/revisao');
        return;
      }

      const pedido = deveCriarReverso(forma, criarReverso);
      const gravadas = tagsParaGravar(baralho, tags);
      const pares = facesDoEnvio(
        {
          front: layout === 'occlusion' ? undefined : front.trim(),
          back: back.trim() || undefined,
        },
        pedido,
      );

      for (let index = 0; index < pares.length; index += 1) {
        const par = pares[index];
        let created: { card_id: string };
        try {
          created = await ReviewService.createCard({
            source: 'flashcard',
            layout,
            subjectId: subjectId!,
            tags: gravadas,
            front: layout === 'occlusion' ? undefined : par.front,
            back: par.back,
            frontImageKey: layout === 'occlusion' ? frontImageKey : undefined,
            masks: layout === 'occlusion' ? masks : undefined,
            occlusionMode: layout === 'occlusion' ? occlusionModeNoFio(studyMode) : undefined,
          });
        } catch (err) {
          if (index === 0) throw err;
          setCriarReverso(false);
          setFront(pares[1]?.front ?? '');
          setBack(pares[1]?.back ?? '');
          notification.error({
            message: 'O cartão foi criado, mas o reverso não.',
            description: 'O primeiro já está em Criados agora. Salvar de novo grava só o que ficou nos campos.',
          });
          return;
        }
        const row = montarCartaoCriado({
          id: created.card_id,
          reverso: index === 1,
          layout,
          subjectId: subjectId!,
          tags: gravadas,
          front: par.front,
          back: par.back,
          frontImageKey,
          masks,
          studyMode,
        });
        setCriados(registrarCriadosAgora([row]));
      }

      const dois = pares.length > 1;
      notification.success({
        message: dois ? 'Dois cartões criados' : 'Flashcard criado',
        description: dois
          ? 'Eles estão em Criados agora. O baralho e as tags continuam para o próximo.'
          : 'Ele está em Criados agora. O baralho e as tags continuam para o próximo.',
      });
      setFront('');
      setBack('');
      setFrontImageKey(undefined);
      setMasks([]);
      setFacePrevia('frente');
    } catch (err: any) {
      notification.error({
        message: 'Erro ao criar o cartão.',
        description: err?.response?.data?.error,
      });
    } finally {
      setSaving(false);
    }
  };

  // Buttons to show: the selected matéria's keywords, plus any already-selected
  // tag (e.g. pre-filled from a picked question) so it stays visible/removable.
  const tagButtons = useMemo(() => {
    const set = new Set<string>(tagOptions.map((o) => o.value));
    tags.forEach((t) => set.add(t));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tagOptions, tags]);

  const subjectName = useMemo(() => {
    const found = (materias ?? []).find((m: { id?: string; name?: string }) => String(m.id) === subjectId);
    return found?.name;
  }, [materias, subjectId]);

  const sugestoes = useMemo(() => {
    const vistas = new Set<string>();
    const lista: string[] = [];
    const acrescentar = (valor: string | undefined) => {
      const limpa = valor?.trim() ?? '';
      if (!limpa || vistas.has(limpa)) return;
      vistas.add(limpa);
      lista.push(limpa);
    };
    tagOptions.forEach((option) => acrescentar(option.value));
    criados.forEach((item) => acrescentar(item.card.tags?.[0]));
    return lista;
  }, [criados, tagOptions]);

  const toggleTag = (t: string) =>
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const abrirCriado = (item: CartaoCriadoAgora) => {
    navigate(`/admin/cartoes/${item.id}/editar`, { state: { card: item.card } });
  };

  return (
    <DefaultLayout>
      <div className="flex min-w-0 flex-col gap-4 px-0 py-4 md:gap-6 md:p-6">
        <div>
          <h1 className="text-xl font-semibold text-ink">
            {isEdit ? 'Editar cartão' : 'Novo cartão'}
          </h1>
          <p className="mt-1 text-sm text-ink-secondary">
            {isEdit
              ? 'Alterar o conteúdo do template atualiza também a cópia de cada aluno, sem mexer no agendamento deles.'
              : 'Flashcards são templates: o conteúdo é gravado uma vez e cada aluno recebe a própria cópia de agendamento.'}
          </p>
        </div>

        <div className="flex min-w-0 max-w-5xl flex-col gap-6 rounded-card border border-edge bg-surface p-4 md:p-6">
          {!isEdit && (
            <div className="flex flex-col gap-2">
              <span id="tipo-cartao-label" className="text-sm font-medium text-ink">
                Tipo
              </span>
              <div
                role="group"
                aria-labelledby="tipo-cartao-label"
                className="flex flex-col gap-2 sm:flex-row"
              >
                {tipoOptions.map((option) => {
                  const chosen = tipo === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={chosen}
                      onClick={() => setTipo(option.value)}
                      className={`w-full rounded-input border px-3 py-2 text-left text-sm font-medium sm:w-auto ${
                        chosen
                          ? 'border-select/[0.42] bg-select/[0.14] text-select-ink'
                          : 'border-edge bg-surface text-ink hover:bg-surface-hover'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {tipo === 'question' ? (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-ink">Questão</span>
              <Select
                showSearch
                filterOption={false}
                onSearch={handleQuestionSearch}
                onChange={handleQuestionSelect}
                value={questionId}
                loading={searching}
                options={questionOptions}
                placeholder="Digite parte do enunciado para buscar"
                notFoundContent={searching ? <Spin size="small" /> : 'Digite para buscar'}
                className="max-w-3xl"
              />
              <p className="text-xs text-ink-muted">
                Questões do banco não viram template — a questão entra no baralho de quem a
                adiciona, igual ao botão “Memorizar” do carrossel.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <span id="forma-autoria-label" className="text-sm font-medium text-ink">
                Forma
              </span>
              <div
                role="radiogroup"
                aria-labelledby="forma-autoria-label"
                onKeyDown={moverForma}
                className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4"
              >
                {TELHAS_AUTORIA.map((telha, index) => {
                  const chosen = forma === telha.id;
                  return (
                    <button
                      key={telha.id}
                      ref={(el) => {
                        formaButtonRefs.current[index] = el;
                      }}
                      type="button"
                      role="radio"
                      aria-checked={chosen}
                      tabIndex={chosen ? 0 : -1}
                      onClick={() => setForma(telha.id)}
                      className={`flex w-full flex-col items-start gap-1 rounded-card border p-3 text-left ${
                        chosen
                          ? 'border-select/[0.42] bg-select/[0.14]'
                          : 'border-edge bg-surface text-ink hover:bg-surface-hover'
                      }`}
                    >
                      <span
                        className={`text-sm font-medium ${chosen ? 'text-select-ink' : 'text-ink'}`}
                      >
                        {telha.nome}
                      </span>
                      <span className="text-xs leading-snug text-ink-muted">{telha.descricao}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 md:max-w-md">
            <span className="text-sm font-medium text-ink">Matéria</span>
            <Select
              showSearch
              optionFilterProp="label"
              value={subjectId}
              onChange={setSubjectId}
              placeholder="Selecione a matéria"
              options={(materias ?? []).map((m: any) => ({
                value: String(m.id),
                label: m.name,
              }))}
            />
          </div>

          {tipo === 'question' && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-ink">Tags</span>
              {tagButtons.length > 0 ? (
                <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto pr-1">
                  {tagButtons.map((t) => (
                    <Button
                      key={t}
                      size="small"
                      shape="round"
                      type="primary"
                      ghost={!tags.includes(t)}
                      onClick={() => toggleTag(t)}
                    >
                      {t}
                    </Button>
                  ))}
                </div>
              ) : (
                <span className="py-1 text-xs text-ink-muted">
                  {subjectId
                    ? 'Nenhuma palavra-chave para esta matéria.'
                    : 'Selecione uma matéria para ver as palavras-chave.'}
                </span>
              )}
            </div>
          )}

          {tipo === 'flashcard' && (
            <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-4">
              <div className="order-3 min-w-0 lg:col-start-1 lg:col-end-3 lg:row-start-1">
                <MetadadosBar
                  baralho={baralho}
                  tags={tags}
                  criarReverso={criarReverso}
                  reversoDisponivel={forma !== 'oclusao'}
                  mostrarReverso={!isEdit}
                  sugestoesBaralho={sugestoes}
                  onBaralhoChange={(value) => {
                    setBaralho(value);
                    const nome = value.trim();
                    if (!nome) return;
                    setTags((prev) => (prev.includes(nome) ? prev.filter((tag) => tag !== nome) : prev));
                  }}
                  onTagsChange={setTags}
                  onCriarReversoChange={setCriarReverso}
                />
              </div>
              <div className="order-1 flex min-w-0 flex-col gap-4 lg:col-start-1 lg:row-start-2">
                {layout === 'occlusion' && (
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-ink">Imagem</span>
                    {/* The editor is itself a row at lg. Beside a 360px preview that
                        row is narrower than the face switch, and the switch
                        paints over the mode radios. Force the editor to
                        stack; the preview stays in the column next to it. */}
                    <div className="min-w-0 [&>div]:!flex-col">
                      <OcclusionEditor
                        frontImageKey={frontImageKey}
                        masks={masks}
                        studyMode={studyMode}
                        onImageChange={setFrontImageKey}
                        onMasksChange={setMasks}
                        onStudyModeChange={setStudyMode}
                        explanation={back}
                        onExplanationChange={setBack}
                      />
                    </div>
                  </div>
                )}
                {rotulos && (
                  <div className="flex max-w-3xl flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <span className="text-sm font-medium text-ink">{rotulos.front}</span>
                      <AlvoDeImagemDaFace
                        ref={frontAlvoRef}
                        onImagemEnviada={(chave) => inserirImagemNoCampo('front', chave)}
                      />
                      <FormatToolbar
                        campo="Frente"
                        onInserir={(formato) => inserirNoCampo('front', formato)}
                        onAbrirAlvo={() => frontAlvoRef.current?.abrir()}
                      />
                      <textarea
                        ref={frontRef}
                        rows={3}
                        value={front}
                        onChange={(e) => setFront(e.target.value)}
                        onFocus={() => setFacePrevia('frente')}
                        className={campoClass}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-sm font-medium text-ink">{rotulos.back}</span>
                      <AlvoDeImagemDaFace
                        ref={backAlvoRef}
                        onImagemEnviada={(chave) => inserirImagemNoCampo('back', chave)}
                      />
                      <FormatToolbar
                        campo="Verso"
                        onInserir={(formato) => inserirNoCampo('back', formato)}
                        onAbrirAlvo={() => backAlvoRef.current?.abrir()}
                      />
                      <textarea
                        ref={backRef}
                        rows={3}
                        value={back}
                        onChange={(e) => setBack(e.target.value)}
                        onFocus={() => setFacePrevia('verso')}
                        className={campoClass}
                      />
                      {rotulos.backHint && (
                        <span className="text-xs text-ink-muted">{rotulos.backHint}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="order-2 min-w-0 lg:col-start-2 lg:row-start-2">
                <CompositorPreview
                  forma={forma}
                  front={front}
                  back={back}
                  frontImageKey={frontImageKey}
                  masks={masks}
                  studyMode={studyMode}
                  subjectId={subjectId}
                  tags={tagsParaGravar(baralho, tags)}
                  subjectName={subjectName}
                  face={facePrevia}
                  onFaceChange={setFacePrevia}
                />
              </div>
              {!isEdit && (
                <div className="order-4 min-w-0 lg:col-start-2 lg:row-start-3">
                  <CriadosAgora itens={criados} onAbrir={abrirCriado} />
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="w-full rounded-input bg-brand px-4 py-2 text-sm font-semibold text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {isEdit ? 'Salvar alterações' : 'Salvar cartão'}
            </button>
            <button
              type="button"
              onClick={() => navigate(isEdit ? '/admin/cartoes' : '/revisao')}
              className="w-full rounded-input border border-edge bg-surface px-4 py-2 text-sm font-medium text-ink hover:bg-surface-hover sm:w-auto"
            >
              Cancelar
            </button>
          </div>

          {!isEdit && tipo !== 'flashcard' && <CriadosAgora itens={criados} onAbrir={abrirCriado} />}
        </div>
      </div>
    </DefaultLayout>
  );
}
